import React, { useMemo, useEffect, useRef, useState } from "react";
import { Container, Card, Table, Badge, Placeholder, Button } from "react-bootstrap";
import { useParams, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useActivityEntriesScoped } from "../hooks/useActivityEntries";
import { fetchActivityEntryStatuses, fetchChurchActivityEntries, fetchDistrictActivityEntries, fetchNationActivityEntries } from "../api/activityEntryApi";
import { ActivityEntryListItem, ActivityEntryStatus } from "../types/activityEntry";
import { FiSend } from "react-icons/fi";
import { SearchBar } from "@core/components/SearchBar";
import NoResult from "@core/components/NoResult";
import EntityCard from "@core/components/EntityCard";
import { Pagination } from "@core/components/Pagination";
import { useDebounce } from "@core/hooks/useDebounce";
import { useSearchUrlSync } from "@core/hooks/useSearchUrlSync";
import { fetchAllPages } from "@core/utils/fetchAllPages";
import { subtleBadgeProps } from "@core/utils/subtleBadge";
import { fetchDepartmentsAsync } from "@features/department/services/departmentSvc";
import { Department } from "@features/department/types/department";
import { buildGroupedDepartmentFilterOptions } from "@features/department/utils/buildGroupedDepartmentFilterOptions";
import {
    canEditActivitySubmission,
    getReportTagStatusLabel,
    getWorkflowStatusLabel,
    getWorkflowStatusVariant,
    isTagOpen,
    normalizeReportWorkflowStatus,
} from "@core/reports/utils/reportWorkflow";
import {
    REPORT_SPA_QUERY_PARAMS,
    buildReportListFilterSearch,
    parseReportListFilterSearch,
} from "@core/reports/constants/reportSpaQueryParams";
import { buildActivityEntrySubmitSearch } from "../constants/activityEntryQueryParams";
import { useReportTags } from "@features/report/tag/hooks/useReportTags";

// ─── Static config ────────────────────────────────────────────────────────────

const SLUG_TO_STATUS_NAME: Record<string, string> = {
    draft: "DRAFT",
    submitted: "SUBMITTED",
    "needs-attention": "NEEDS_ATTENTION",
};

const SLUG_LABELS: Record<string, { label: string; variant: string }> = {
    all: { label: "All", variant: "secondary" },
    draft: { label: "Draft", variant: getWorkflowStatusVariant("DRAFT") },
    submitted: { label: "Submitted", variant: getWorkflowStatusVariant("SUBMITTED") },
    "needs-attention": {
        label: "Needs Attention",
        variant: getWorkflowStatusVariant("NEEDS_ATTENTION"),
    },
};

function getTemplateOptions(items: ActivityEntryListItem[]) {
    return Array.from(
        new Map(
            items.map((item) => [
                item.report_template_id,
                {
                    value: item.report_template_id,
                    label: item.report_template_name,
                },
            ])
        ).values()
    ).sort((a, b) => a.label.localeCompare(b.label));
}

function getDepartmentOptions(items: Department[]) {
    const unique = Array.from(
        new Map(
            items
                .filter((item) => Number(item.department_id) > 0)
                .map((item) => [item.department_id, item])
        ).values()
    );
    return buildGroupedDepartmentFilterOptions(unique);
}

// ─── Component ────────────────────────────────────────────────────────────────

type ScopeType = 'CHURCH' | 'DISTRICT' | 'NATION';

const ActivitySubmissionsList: React.FC = () => {
    const { churchId: urlChurchId, districtId: urlDistrictId, nationId: urlNationId } = useParams<{
        churchId?: string;
        districtId?: string;
        nationId?: string;
    }>();
    const location = useLocation();
    const navigate = useNavigate();

    const { scope, scopeId, basePath } = useMemo((): { scope: ScopeType; scopeId: number; basePath: string } => {
        if (urlChurchId) return { scope: 'CHURCH', scopeId: Number(urlChurchId) || 0, basePath: `/church/${urlChurchId}` };
        if (urlDistrictId) return { scope: 'DISTRICT', scopeId: Number(urlDistrictId) || 0, basePath: `/district/${urlDistrictId}` };
        if (urlNationId) return { scope: 'NATION', scopeId: Number(urlNationId) || 0, basePath: `/nation/${urlNationId}` };
        return { scope: 'CHURCH', scopeId: 0, basePath: '/church/0' };
    }, [urlChurchId, urlDistrictId, urlNationId]);

    // ── Status list ────────────────────────────────────────────────────────
    const [statuses, setStatuses] = useState<ActivityEntryStatus[]>([]);
    const [statusesLoading, setStatusesLoading] = useState(true);

    useEffect(() => {
        fetchActivityEntryStatuses().then((res) => {
            if (res.isSuccess()) {
                const payload = res.getData();
                const items = Array.isArray(payload) ? payload : payload?.data ?? [];
                setStatuses(items);
            }
        }).catch(() => {
            // silently handle
        }).finally(() => {
            setStatusesLoading(false);
        });
    }, []);

    // ── Status slug derived from the last path segment ─────────────────────
    const statusSlug = useMemo(() => {
        const segments = location.pathname.split("/").filter(Boolean);
        return segments[segments.length - 1] || "draft";
    }, [location.pathname]);

    const slugLabel = SLUG_LABELS[statusSlug] ?? SLUG_LABELS.draft;
    const isAllView = statusSlug === "all";
    const targetStatusName = SLUG_TO_STATUS_NAME[statusSlug] ?? "DRAFT";

    const activityEntryStatusId = useMemo(() => {
        if (isAllView) return undefined;
        const match = statuses.find(
            (s) => s.status_name.toUpperCase() === targetStatusName
        );
        return match?.activity_entry_status_id;
    }, [statuses, targetStatusName, isAllView]);

    // ── Search state (initialised from URL) ────────────────────────────────
    const [urlParams] = useSearchParams();
    const [searchFreetext, setSearchFreetext] = useState(() => urlParams.get("q") ?? "");
    const debouncedFreetext = useDebounce(searchFreetext, 300);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const [reportTemplateFilterId, setReportTemplateFilterId] = useState<number | undefined>(() => {
        return parseReportListFilterSearch(urlParams).reportTemplateId;
    });
    const [reportTagFilterId, setReportTagFilterId] = useState<number | undefined>(() => {
        return parseReportListFilterSearch(urlParams).reportTagId;
    });
    const [departmentFilterId, setDepartmentFilterId] = useState<number | undefined>(() => {
        return parseReportListFilterSearch(urlParams).departmentId;
    });
    const [templateOptions, setTemplateOptions] = useState<Array<{ value: number; label: string }>>([]);
    const [departmentOptions, setDepartmentOptions] = useState<Array<{ value: number; label: string }>>([]);
    const [departmentsLoading, setDepartmentsLoading] = useState(false);
    const previousPathnameRef = useRef(location.pathname);
    const loadedFilterOptionsKeyRef = useRef("");

    const reportTagScope = useMemo(() => {
        if (scope === 'NATION') return { nationId: scopeId };
        if (scope === 'DISTRICT') return { districtId: scopeId };
        return { churchId: scopeId };
    }, [scope, scopeId]);

    const { data: reportTags = [] } = useReportTags({
        all: true,
        ...reportTagScope,
    });

    const reportTagOptions = useMemo(
        () =>
            reportTags.map((tag: { report_tag_id: number; name: string; report_tag_status_name?: string | null }) => {
                const statusName = tag.report_tag_status_name;
                const statusLabel = getReportTagStatusLabel(statusName);
                return {
                    value: tag.report_tag_id,
                    label:
                        statusLabel && !isTagOpen(statusName)
                            ? `${tag.name} (${statusLabel})`
                            : tag.name,
                };
            }),
        [reportTags]
    );

    useSearchUrlSync({
        query: debouncedFreetext,
        filters: buildReportListFilterSearch({
            departmentId: departmentFilterId,
            reportTemplateId: reportTemplateFilterId,
            reportTagId: reportTagFilterId,
        }),
        page,
    });

    useEffect(() => {
        if (previousPathnameRef.current === location.pathname) return;

        previousPathnameRef.current = location.pathname;
        setSearchFreetext("");
        setDepartmentFilterId(undefined);
        setReportTemplateFilterId(undefined);
        setReportTagFilterId(undefined);
        setPage(1);
    }, [location.pathname]);

    // ── API query params ────────────────────────────────────────────────────
    const queryParams = useMemo(() => ({
        activityEntryStatus: activityEntryStatusId,
        departmentId: departmentFilterId,
        page,
        perPage,
        freetext: debouncedFreetext.trim() || undefined,
        reportTemplateId: reportTemplateFilterId,
        reportTagId: reportTagFilterId,
    }), [activityEntryStatusId, departmentFilterId, page, perPage, debouncedFreetext, reportTemplateFilterId, reportTagFilterId]);

    const enabledScopeId = isAllView ? scopeId : (activityEntryStatusId !== undefined ? scopeId : 0);

    const { data, loading, pagination } = useActivityEntriesScoped({
        scope,
        scopeId: enabledScopeId,
        params: queryParams,
    });

    const filterOptionsScopeKey = useMemo(
        () => `${scope}:${scopeId}:${isAllView ? "all" : activityEntryStatusId ?? "pending"}`,
        [activityEntryStatusId, isAllView, scope, scopeId]
    );

    useEffect(() => {
        let cancelled = false;
        setDepartmentsLoading(true);

        const loadDepartmentOptions = async () => {
            try {
                const scopeParams = scope === 'CHURCH'
                    ? { churchId: scopeId }
                    : scope === 'DISTRICT'
                        ? { districtId: scopeId }
                        : { nationId: scopeId };

                const items = await fetchAllPages<Department>((nextPage) =>
                    fetchDepartmentsAsync({
                        all: true,
                        ...scopeParams,
                        page: nextPage,
                        perPage: 100,
                    })
                );

                if (cancelled) return;
                setDepartmentOptions(getDepartmentOptions(items));
            } catch {
                if (!cancelled) {
                    setDepartmentOptions([]);
                }
            } finally {
                if (!cancelled) {
                    setDepartmentsLoading(false);
                }
            }
        };

        void loadDepartmentOptions();

        return () => {
            cancelled = true;
        };
    }, [scope, scopeId]);

    useEffect(() => {
        if (!isAllView && activityEntryStatusId === undefined) {
            loadedFilterOptionsKeyRef.current = "";
            setTemplateOptions([]);
            return;
        }

        const canUseVisibleRowsForOptions =
            !debouncedFreetext.trim() &&
            !reportTemplateFilterId &&
            !reportTagFilterId &&
            page === 1 &&
            (pagination?.last_page ?? 0) <= 1;

        if (canUseVisibleRowsForOptions) {
            loadedFilterOptionsKeyRef.current = filterOptionsScopeKey;
            setTemplateOptions(getTemplateOptions(data));
            return;
        }

        if (loadedFilterOptionsKeyRef.current === filterOptionsScopeKey || loading || !pagination) {
            return;
        }

        let cancelled = false;

        const loadFilterOptions = async () => {
            try {
                const fetchFn = (nextPage: number) => {
                    const params = {
                        activityEntryStatus: isAllView ? undefined : activityEntryStatusId,
                        page: nextPage,
                        perPage: 100,
                    };
                    if (scope === 'CHURCH') return fetchChurchActivityEntries(scopeId, params);
                    if (scope === 'DISTRICT') return fetchDistrictActivityEntries(scopeId, params);
                    return fetchNationActivityEntries(scopeId, params);
                };

                const items = await fetchAllPages<ActivityEntryListItem>(fetchFn);

                if (cancelled) return;

                loadedFilterOptionsKeyRef.current = filterOptionsScopeKey;
                setTemplateOptions(getTemplateOptions(items));
            } catch {
                if (!cancelled) {
                    setTemplateOptions([]);
                }
            }
        };

        void loadFilterOptions();

        return () => {
            cancelled = true;
        };
    }, [
        activityEntryStatusId,
        scope,
        scopeId,
        data,
        debouncedFreetext,
        filterOptionsScopeKey,
        isAllView,
        loading,
        page,
        pagination,
        reportTagFilterId,
        reportTemplateFilterId,
    ]);

    const isLoading = statusesLoading || (!isAllView && activityEntryStatusId === undefined) || loading;

    // ── Handlers ───────────────────────────────────────────────────────────
    const handlePageChange = (newPage: number) => setPage(newPage);

    const handlePerPageChange = (newPerPage: number) => {
        setPerPage(newPerPage);
        setPage(1);
    };

    const handleClearSearch = () => {
        setSearchFreetext("");
        setDepartmentFilterId(undefined);
        setReportTemplateFilterId(undefined);
        setReportTagFilterId(undefined);
        setPage(1);
    };

    const handleApplyFilters = (filters: Record<string, any>) => {
        setDepartmentFilterId(
            filters[REPORT_SPA_QUERY_PARAMS.DEPARTMENT_ID]
                ? Number(filters[REPORT_SPA_QUERY_PARAMS.DEPARTMENT_ID])
                : undefined
        );
        setReportTemplateFilterId(
            filters[REPORT_SPA_QUERY_PARAMS.TEMPLATE_ID]
                ? Number(filters[REPORT_SPA_QUERY_PARAMS.TEMPLATE_ID])
                : undefined
        );
        setReportTagFilterId(
            filters[REPORT_SPA_QUERY_PARAMS.TAG_ID]
                ? Number(filters[REPORT_SPA_QUERY_PARAMS.TAG_ID])
                : undefined
        );
        setPage(1);
    };

    const paginationMeta = useMemo(() => {
        if (!pagination || pagination.total === 0) return null;
        return pagination;
    }, [pagination]);

    const filterFields = useMemo(() => [
        {
            name: REPORT_SPA_QUERY_PARAMS.DEPARTMENT_ID,
            label: "Department",
            type: "select" as const,
            options: departmentOptions,
            colWidth: 4,
        },
        {
            name: REPORT_SPA_QUERY_PARAMS.TEMPLATE_ID,
            label: "Report Template",
            type: "select" as const,
            options: templateOptions,
            colWidth: 4,
        },
        {
            name: REPORT_SPA_QUERY_PARAMS.TAG_ID,
            label: "Reporting Period",
            type: "select" as const,
            options: reportTagOptions,
            colWidth: 4,
        },
    ], [departmentOptions, templateOptions, reportTagOptions]);

    const activeFilterCount =
        (departmentFilterId ? 1 : 0) +
        (reportTemplateFilterId ? 1 : 0) +
        (reportTagFilterId ? 1 : 0);

    const handleRowClick = (item: typeof data[number]) => {
        navigate(
            `${basePath}/reports/submit?${buildActivityEntrySubmitSearch({
                reportTemplateId: item.report_template_id,
                reportTagId: item.report_tag_id,
                departmentId: item.department_id,
            }).toString()}`
        );
    };

    // ── Main render ────────────────────────────────────────────────────────
    return (
        <Container fluid className="py-4">
            {/* Page title */}
            <div className="d-flex align-items-center gap-2 mb-3">
                <FiSend size={20} className="text-primary" aria-hidden="true" />
                <h1 className="ntcog-page-heading mb-0">Activity Submissions</h1>
            </div>

            {/* Search bar */}
            <div className="ntcog-list-search-section">
                <SearchBar
                    placeholder={
                        isAllView
                            ? "Search all activity submissions…"
                            : `Search within ${slugLabel.label} submissions…`
                    }
                    initialQuery={searchFreetext}
                    initialFilters={buildReportListFilterSearch({
                        departmentId: departmentFilterId,
                        reportTemplateId: reportTemplateFilterId,
                        reportTagId: reportTagFilterId,
                    })}
                    onQueryChange={(q) => { setSearchFreetext(q); setPage(1); }}
                    filterFields={filterFields}
                    onFiltersApply={handleApplyFilters}
                    onClear={handleClearSearch}
                    loading={loading || departmentsLoading}
                    activeFilterCount={activeFilterCount}
                    testId="activity-submissions-search"
                />

            </div>

            {/* Data table */}
            <Card className="shadow-sm ntcog-card ntcog-list-table-card">
                <Card.Body className="p-0">
                    <div className="d-none d-md-block">
                        <Table responsive hover size="sm" className="mb-0 align-middle table-striped ntcog-table">
                            <thead className="table-light">
                                <tr>
                                    <th style={{ width: 56 }}>#</th>
                                    <th>Report Template</th>
                                    <th>Reporting Period</th>
                                    <th>Period Range</th>
                                    <th style={{ width: 80 }}>Entries</th>
                                    <th style={{ width: 130 }}>Status</th>
                                    <th style={{ width: 120 }}>Last Updated</th>
                                    <th style={{ width: 140 }} className="text-end">Action</th>
                                </tr>
                            </thead>
                            <tbody className="table-group-divider" style={{ opacity: isLoading && data.length > 0 ? 0.5 : 1, transition: "opacity 0.2s ease" }}>
                                {isLoading && data.length === 0 && Array.from({ length: 6 }).map((_, i) => (
                                    <tr key={`ph-${i}`}>
                                        <td><Placeholder xs={4} /></td>
                                        <td><Placeholder xs={7} /></td>
                                        <td><Placeholder xs={5} /></td>
                                        <td><Placeholder xs={6} /></td>
                                        <td><Placeholder xs={4} /></td>
                                        <td><Placeholder xs={4} /></td>
                                        <td><Placeholder xs={5} /></td>
                                        <td><Placeholder xs={5} /></td>
                                    </tr>
                                ))}
                                {!isLoading && data.length === 0 && (
                                    <tr>
                                        <td colSpan={8}>
                                            <NoResult
                                                line_1={`No ${slugLabel.label} Submissions`}
                                                line_2={
                                                    debouncedFreetext.trim() || departmentFilterId || reportTemplateFilterId || reportTagFilterId
                                                        ? "No submissions match your search. Try adjusting or clearing filters."
                                                        : isAllView
                                                            ? "There are no activity submissions at this time."
                                                            : `There are no "${slugLabel.label}" submissions at this time.`
                                                }
                                            />
                                        </td>
                                    </tr>
                                )}
                                {data.map((item, i) => {
                                    const rowNumber = paginationMeta?.from != null ? paginationMeta.from + i : i + 1;

                                    return (
                                    <tr key={`${item.report_tag_id}-${item.report_template_id}`}>
                                        <td className="text-muted">{rowNumber}</td>
                                        <td className="fw-semibold">{item.report_template_name}</td>
                                        <td>{item.report_tag_name}</td>
                                        <td className="text-muted small">
                                            {item.report_tag_since} — {item.report_tag_until}
                                        </td>
                                        <td>{item.entry_count}</td>
                                        <td>
                                            <Badge {...subtleBadgeProps(getWorkflowStatusVariant(item.status_name))}>
                                                {getWorkflowStatusLabel(item.status_name)}
                                            </Badge>
                                            <div className="text-muted small mt-1">
                                                {canEditActivitySubmission(item.status_name)
                                                    ? "Editable"
                                                    : "View only"}
                                            </div>
                                        </td>
                                        <td className="text-muted small">
                                            {item.last_updated_at
                                                ? new Date(item.last_updated_at).toLocaleDateString()
                                                : "—"}
                                        </td>
                                        <td className="text-end">
                                            <Button
                                                variant={canEditActivitySubmission(item.status_name) ? "outline-primary" : "outline-secondary"}
                                                size="sm"
                                                className="btn-action"
                                                onClick={() => handleRowClick(item)}
                                            >
                                                {(() => {
                                                    const normalizedStatus = normalizeReportWorkflowStatus(item.status_name);
                                                    if (normalizedStatus === "DRAFT") return "Continue Draft";
                                                    if (normalizedStatus === "NEEDS_ATTENTION") return "Review & Edit";
                                                    return `View ${getWorkflowStatusLabel(item.status_name)}`;
                                                })()}
                                            </Button>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>

                    {/* Mobile (< md): stacked cards */}
                    <div className="ntcog-card-list d-md-none" data-testid="activity-submissions-cards">
                        {isLoading && data.length === 0 && Array.from({ length: 6 }).map((_, i) => (
                            <div key={`ph-card-${i}`} className="ntcog-card-list__item">
                                <EntityCard
                                    testId={`activity-submission-card-skeleton-${i}`}
                                    title={<Placeholder as="span" animation="glow"><Placeholder xs={6} /></Placeholder>}
                                />
                            </div>
                        ))}
                        {!isLoading && data.length === 0 && (
                            <NoResult
                                line_1={`No ${slugLabel.label} Submissions`}
                                line_2={
                                    debouncedFreetext.trim() || departmentFilterId || reportTemplateFilterId || reportTagFilterId
                                        ? "No submissions match your search. Try adjusting or clearing filters."
                                        : isAllView
                                            ? "There are no activity submissions at this time."
                                            : `There are no "${slugLabel.label}" submissions at this time.`
                                }
                            />
                        )}
                        {data.map((item) => {
                            const editable = canEditActivitySubmission(item.status_name);
                            const normalizedStatus = normalizeReportWorkflowStatus(item.status_name);
                            const actionLabel = normalizedStatus === "DRAFT"
                                ? "Continue Draft"
                                : normalizedStatus === "NEEDS_ATTENTION"
                                    ? "Review & Edit"
                                    : `View ${getWorkflowStatusLabel(item.status_name)}`;
                            return (
                                <div key={`${item.report_tag_id}-${item.report_template_id}`} className="ntcog-card-list__item">
                                    <EntityCard
                                        testId={`activity-submission-card-${item.report_tag_id}-${item.report_template_id}`}
                                        ariaLabel={`Activity submission ${item.report_template_name}`}
                                        title={<span className="fw-semibold">{item.report_template_name}</span>}
                                        details={[
                                            { label: "Reporting Period", value: item.report_tag_name },
                                            { label: "Period", value: `${item.report_tag_since} — ${item.report_tag_until}` },
                                            { label: "Updated", value: item.last_updated_at ? new Date(item.last_updated_at).toLocaleDateString() : "—" },
                                        ]}
                                        status={
                                            <>
                                                <Badge {...subtleBadgeProps(getWorkflowStatusVariant(item.status_name))}>
                                                    {getWorkflowStatusLabel(item.status_name)}
                                                </Badge>
                                                <span className="text-muted small">{editable ? "Editable" : "View only"}</span>
                                            </>
                                        }
                                        primaryAction={
                                            <Button
                                                variant={editable ? "outline-primary" : "outline-secondary"}
                                                size="sm"
                                                className="btn-action w-100"
                                                onClick={() => handleRowClick(item)}
                                            >
                                                {actionLabel}
                                            </Button>
                                        }
                                    />
                                </div>
                            );
                        })}
                    </div>
                </Card.Body>
            </Card>

            {paginationMeta && paginationMeta.last_page > 1 && (
                <Pagination
                    meta={paginationMeta}
                    onPageChange={handlePageChange}
                    showInfo={true}
                    showPerPageSelector={true}
                    perPageOptions={[10, 20, 50, 100]}
                    onPerPageChange={handlePerPageChange}
                    className="mt-3"
                />
            )}
        </Container>
    );
};

export default ActivitySubmissionsList;
